import { useState, useCallback } from 'react'
import { apiFetch } from '../../lib/api-client.js'
import { getSocket } from '../../lib/socket-client.js'
import { useChatStore } from '../../stores/useChatStore.js'
import { CLIENT_EVENTS } from '../../types/socket-events.js'
import { ConversationHeader } from './ConversationHeader.js'
import { ReplyBox } from './ReplyBox.js'
import { Avatar } from '../../components/index.js'
import type { Chat } from '../../types/chat.js'
import type { UserProfile } from '../../types/user.js'

interface PendingConversationPanelProps {
  targetUser: UserProfile
  currentUser: UserProfile
  users: UserProfile[]
  onChatCreated: (chatId: string) => void
}

export function PendingConversationPanel({
  targetUser,
  currentUser,
  users,
  onChatCreated,
}: PendingConversationPanelProps) {
  const [isSending, setIsSending] = useState(false)
  const addChat = useChatStore((s) => s.addChat)

  const handleSend = useCallback(
    async (content: string, attachments?: Array<{ url: string; type: string; name: string }>) => {
      if (isSending) return
      setIsSending(true)

      try {
        // Step 1: Create the chat
        const chat = await apiFetch<Chat>('/api/internal-chats', {
          method: 'POST',
          body: JSON.stringify({ type: 'direct', targetUserId: targetUser.id }),
        })
        addChat(chat)

        // Step 2: Join socket room and send message
        const socket = getSocket()
        if (socket) {
          socket.emit(CLIENT_EVENTS.JOIN_CHAT, { chatId: chat.id })
          socket.emit(CLIENT_EVENTS.SEND_MESSAGE, {
            chatId: chat.id,
            content: content || undefined,
            attachments: attachments ?? undefined,
          })
        }

        // Step 3: Transition to real chat
        onChatCreated(chat.id)
      } catch (err) {
        console.error('[ChatInterno] Erro ao criar conversa:', err)
        setIsSending(false)
      }
    },
    [targetUser.id, addChat, onChatCreated, isSending],
  )

  // Build a fake chat object for the header
  const fakeChat = {
    type: 'direct' as const,
    members: [
      { userId: currentUser.id, role: 'member' },
      { userId: targetUser.id, role: 'member' },
    ],
  }

  return (
    <div className="flex h-full flex-col">
      <ConversationHeader chat={fakeChat as Chat} displayName={targetUser.name} />

      {/* Empty state — waiting for first message */}
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4">
        <Avatar name={targetUser.name} avatarUrl={targetUser.avatarUrl} size="lg" />
        <p className="text-sm text-(--color-text-secondary)">
          Inicie uma conversa com <span className="font-medium text-(--color-text-primary)">{targetUser.name}</span>
        </p>
      </div>

      <ReplyBox onSend={handleSend} disabled={isSending} />
    </div>
  )
}
